import { supabase } from './supabase';
import axios from 'axios';

export interface Integration {
  id: string;
  name: string;
  provider: string;
  status: 'active' | 'inactive' | 'error';
  config: Record<string, any>;
  created_at: string;
  updated_at: string;
  last_sync?: string;
  user_id: string;
}

export interface IntegrationProvider {
  id: string;
  name: string;
  description: string;
  logo: string;
  category: 'project-management' | 'analytics' | 'communication' | 'development' | 'other';
  configFields: IntegrationConfigField[];
  capabilities: string[];
}

export interface IntegrationConfigField {
  name: string;
  label: string;
  type: 'text' | 'password' | 'select' | 'checkbox';
  required: boolean;
  placeholder?: string;
  options?: { label: string; value: string }[];
  helpText?: string;
}

// Available integration providers
export const INTEGRATION_PROVIDERS: IntegrationProvider[] = [
  {
    id: 'jira',
    name: 'Jira',
    description: 'Connect to Jira to sync issues, epics, and sprints with your ADAPT backlog.',
    logo: 'https://cdn.worldvectorlogo.com/logos/jira-1.svg',
    category: 'project-management',
    configFields: [
      {
        name: 'domain',
        label: 'Jira Domain',
        type: 'text',
        required: true,
        placeholder: 'your-domain.atlassian.net',
        helpText: 'Your Atlassian domain name'
      },
      {
        name: 'apiToken',
        label: 'API Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your Jira API token',
        helpText: 'Generate an API token from your Atlassian account settings'
      },
      {
        name: 'email',
        label: 'Email',
        type: 'text',
        required: true,
        placeholder: 'your-email@example.com',
        helpText: 'The email associated with your Atlassian account'
      },
      {
        name: 'projectKey',
        label: 'Project Key',
        type: 'text',
        required: true,
        placeholder: 'PROJ',
        helpText: 'The key of the Jira project to sync with'
      }
    ],
    capabilities: [
      'Sync backlog items with Jira issues',
      'Import Jira epics as strategic goals',
      'Update issue status bidirectionally',
      'Link experiments to Jira issues'
    ]
  },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Connect to GitHub to link repositories, issues, and pull requests to your experiments and backlog.',
    logo: 'https://cdn.worldvectorlogo.com/logos/github-icon-1.svg',
    category: 'development',
    configFields: [
      {
        name: 'accessToken',
        label: 'Personal Access Token',
        type: 'password',
        required: true,
        placeholder: 'Enter your GitHub personal access token',
        helpText: 'Generate a token with repo and user scopes'
      },
      {
        name: 'organization',
        label: 'Organization',
        type: 'text',
        required: false,
        placeholder: 'your-org',
        helpText: 'Optional: Your GitHub organization name'
      },
      {
        name: 'repositories',
        label: 'Repositories',
        type: 'text',
        required: true,
        placeholder: 'repo1,repo2,repo3',
        helpText: 'Comma-separated list of repositories to connect'
      }
    ],
    capabilities: [
      'Link backlog items to GitHub issues',
      'Track development progress through PRs',
      'Connect experiments to code changes',
      'Sync issue status bidirectionally'
    ]
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Connect to Slack to receive notifications and updates about your ADAPT activities.',
    logo: 'https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg',
    category: 'communication',
    configFields: [
      {
        name: 'webhookUrl',
        label: 'Webhook URL',
        type: 'text',
        required: true,
        placeholder: 'https://hooks.slack.com/services/...',
        helpText: 'Create an incoming webhook in your Slack workspace'
      },
      {
        name: 'channel',
        label: 'Default Channel',
        type: 'text',
        required: true,
        placeholder: '#channel-name',
        helpText: 'The default channel to send notifications to'
      },
      {
        name: 'notifyExperiments',
        label: 'Experiment Notifications',
        type: 'checkbox',
        required: false,
        helpText: 'Send notifications for experiment updates'
      },
      {
        name: 'notifyBacklog',
        label: 'Backlog Notifications',
        type: 'checkbox',
        required: false,
        helpText: 'Send notifications for backlog updates'
      },
      {
        name: 'notifyInsights',
        label: 'Market Insight Notifications',
        type: 'checkbox',
        required: false,
        helpText: 'Send notifications for new market insights'
      }
    ],
    capabilities: [
      'Receive notifications for new experiments',
      'Get alerts for market insights',
      'Backlog item status updates',
      'Weekly summary reports'
    ]
  },
  {
    id: 'google-analytics',
    name: 'Google Analytics',
    description: 'Connect to Google Analytics to import user behavior data and metrics into your experiments.',
    logo: 'https://cdn.worldvectorlogo.com/logos/google-analytics-4.svg',
    category: 'analytics',
    configFields: [
      {
        name: 'clientEmail',
        label: 'Service Account Email',
        type: 'text',
        required: true,
        placeholder: 'service-account@project.iam.gserviceaccount.com',
        helpText: 'Email address of your Google service account'
      },
      {
        name: 'privateKey',
        label: 'Private Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your private key',
        helpText: 'Private key from your service account JSON file'
      },
      {
        name: 'viewId',
        label: 'View ID',
        type: 'text',
        required: true,
        placeholder: '123456789',
        helpText: 'The Google Analytics view ID to pull data from'
      }
    ],
    capabilities: [
      'Import user behavior metrics',
      'Correlate experiment results with analytics',
      'Track conversion metrics',
      'Monitor user engagement'
    ]
  },
  {
    id: 'linear',
    name: 'Linear',
    description: 'Connect to Linear to sync issues and projects with your ADAPT backlog and experiments.',
    logo: 'https://asset.brandfetch.io/idFdo8ulhr/idzj70K0HS.png',
    category: 'project-management',
    configFields: [
      {
        name: 'apiKey',
        label: 'API Key',
        type: 'password',
        required: true,
        placeholder: 'Enter your Linear API key',
        helpText: 'Generate an API key from your Linear account settings'
      },
      {
        name: 'teamId',
        label: 'Team ID',
        type: 'text',
        required: true,
        placeholder: 'Enter your Linear team ID',
        helpText: 'The ID of the team whose issues you want to sync'
      }
    ],
    capabilities: [
      'Sync backlog items with Linear issues',
      'Import Linear projects as strategic goals',
      'Update issue status bidirectionally',
      'Link experiments to Linear issues'
    ]
  }
];

// Get all integrations for the current user
export const getUserIntegrations = async (): Promise<Integration[]> => {
  try {
    const { data, error } = await supabase
      .from('integrations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching integrations:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error fetching integrations:', error);
    return [];
  }
};

// Get a specific integration by ID
export const getIntegrationById = async (id: string): Promise<Integration | null> => {
  try {
    const { data, error } = await supabase
      .from('integrations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching integration:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error fetching integration:', error);
    return null;
  }
};

// Create a new integration
export const createIntegration = async (
  provider: string,
  name: string,
  config: Record<string, any>
): Promise<Integration | null> => {
  try {
    const { data, error } = await supabase
      .from('integrations')
      .insert({
        provider,
        name,
        config,
        status: 'inactive'
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating integration:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error creating integration:', error);
    return null;
  }
};

// Update an existing integration
export const updateIntegration = async (
  id: string,
  updates: Partial<Omit<Integration, 'id' | 'created_at' | 'user_id'>>
): Promise<Integration | null> => {
  try {
    const { data, error } = await supabase
      .from('integrations')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating integration:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error updating integration:', error);
    return null;
  }
};

// Delete an integration
export const deleteIntegration = async (id: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('integrations')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting integration:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error deleting integration:', error);
    return false;
  }
};

// Test an integration connection
export const testIntegrationConnection = async (
  provider: string,
  config: Record<string, any>
): Promise<{ success: boolean; message: string }> => {
  try {
    // In a real implementation, this would call a backend endpoint
    // For demo purposes, we'll simulate a successful connection
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Simulate validation based on provider
    switch (provider) {
      case 'jira':
        if (!config.domain || !config.apiToken || !config.email) {
          return { 
            success: false, 
            message: 'Missing required Jira configuration fields' 
          };
        }
        break;
      case 'github':
        if (!config.accessToken || !config.repositories) {
          return { 
            success: false, 
            message: 'Missing required GitHub configuration fields' 
          };
        }
        break;
      case 'slack':
        if (!config.webhookUrl || !config.channel) {
          return { 
            success: false, 
            message: 'Missing required Slack configuration fields' 
          };
        }
        break;
      // Add cases for other providers
    }
    
    return { 
      success: true, 
      message: 'Connection successful! Integration is ready to use.' 
    };
  } catch (error) {
    console.error('Error testing integration:', error);
    return { 
      success: false, 
      message: 'Failed to test integration connection' 
    };
  }
};

// Sync data from an integration
export const syncIntegrationData = async (id: string): Promise<{ success: boolean; message: string }> => {
  try {
    // In a real implementation, this would call a backend endpoint
    // For demo purposes, we'll simulate a successful sync
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    // Update the last_sync timestamp
    const { error } = await supabase
      .from('integrations')
      .update({
        last_sync: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating integration last_sync:', error);
      return { 
        success: false, 
        message: 'Failed to update sync timestamp' 
      };
    }
    
    return { 
      success: true, 
      message: 'Data synchronized successfully!' 
    };
  } catch (error) {
    console.error('Error syncing integration data:', error);
    return { 
      success: false, 
      message: 'Failed to sync integration data' 
    };
  }
};

// Get integration provider details
export const getIntegrationProvider = (providerId: string): IntegrationProvider | undefined => {
  return INTEGRATION_PROVIDERS.find(provider => provider.id === providerId);
};